// Automated Reminder Service for Appointment Notifications
// Handles SMS/WhatsApp reminders 24 hours before appointments with confirmation tracking

import { differenceInHours, parseISO, format } from 'date-fns';
import { ar } from 'date-fns/locale';

class ReminderService {
  constructor() {
    this.reminders = [];
    this.confirmations = [];
    this.listeners = [];
    this.checkInterval = null;
    this.logoSignatureEnabled = true; // Default enabled
    this.companyLogoUrl = window.location?.origin + '/assets/images/2arddgoldlogotransembo-1768095394670.png';
    this.companyName = 'ARDDjo';
    this.companyFooter = {
      enabled: true,
      text: 'ARDDjo Real Estate | عقارات ARDDjo',
      phone: '+962 6 XXX XXXX',
      email: 'info@arddjo.com',
      website: 'www.arddjo.com'
    };
  }

  // Subscribe to reminder updates
  subscribe(callback) {
    this.listeners?.push(callback);
    return () => {
      this.listeners = this.listeners?.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  notifyListeners() {
    this.listeners?.forEach(callback => callback({
      reminders: this.reminders,
      confirmations: this.confirmations
    }));
  }

  // Start automated reminder checking (runs every hour)
  startAutomatedChecking(appointments) {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    // Initial check
    this.checkAndSendReminders(appointments);

    // Check every hour
    this.checkInterval = setInterval(() => {
      this.checkAndSendReminders(appointments);
    }, 60 * 60 * 1000); // 1 hour
  }

  // Stop automated checking
  stopAutomatedChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  // Check appointments and send reminders for those 24 hours away
  checkAndSendReminders(appointments) {
    const now = new Date();

    appointments?.forEach(appointment => {
      // Skip if already sent, cancelled, or no phone number
      if (appointment?.reminderSent || appointment?.status === 'cancelled' || !appointment?.clientPhone) {
        return;
      }

      // Calculate hours until appointment
      const appointmentDateTime = parseISO(`${appointment?.date}T${appointment?.time}`);
      const hoursUntil = differenceInHours(appointmentDateTime, now);

      // Send reminder if between 23-25 hours away (24-hour window with buffer)
      if (hoursUntil >= 23 && hoursUntil <= 25) {
        this.sendReminder(appointment);
      }
    });
  }

  // Send SMS/WhatsApp reminder
  async sendReminder(appointment, channel = 'whatsapp') {
    try {
      let message = this.buildReminderMessage(appointment);
      
      // Simulate API call to Twilio
      const result = await this.sendViaChannel(appointment?.clientPhone, message, channel);

      // Track reminder
      const reminder = {
        id: `REM-${Date.now()}`,
        appointmentId: appointment?.id,
        clientName: appointment?.clientName,
        clientPhone: appointment?.clientPhone,
        channel: channel,
        message: message,
        sentAt: new Date()?.toISOString(),
        status: 'sent',
        confirmationStatus: 'pending',
        confirmationReceived: false
      };

      this.reminders?.push(reminder);
      this.notifyListeners();

      return { success: true, reminder };
    } catch (error) {
      console.error('Failed to send reminder:', error);
      return { success: false, error: error?.message };
    }
  }

  // Build reminder message in Arabic
  buildReminderMessage(appointment) {
    const appointmentDate = format(parseISO(appointment?.date), 'EEEE، d MMMM yyyy', { locale: ar });
    
    let message = `مرحباً ${appointment?.clientName}،\n\n` +
           `تذكير بموعدك لمعاينة العقار:\n` +
           `📍 ${appointment?.propertyAddress}\n` +
           `📅 ${appointmentDate}\n` +
           `🕐 ${appointment?.time}\n` +
           `👤 مع: ${appointment?.staffName}\n\n` +
           `للتأكيد، يرجى الرد:\n` +
           `1 - تأكيد الحضور\n` +
           `2 - إلغاء الموعد\n` +
           `3 - تغيير الموعد\n\n` +
           `شكراً لك - ${this.companyName}`;

    // Add logo signature if enabled
    if (this.logoSignatureEnabled) {
      message += `\n\n🏢 ${this.companyLogoUrl}`;
    }

    // Add footer if enabled
    if (this.companyFooter?.enabled) {
      message += `\n\n${this.companyFooter?.text}`;
      if (this.companyFooter?.phone) message += `\n📞 ${this.companyFooter?.phone}`;
      if (this.companyFooter?.email) message += `\n📧 ${this.companyFooter?.email}`;
      if (this.companyFooter?.website) message += `\n🌐 ${this.companyFooter?.website}`;
    }

    return message;
  }

  // Simulate sending via Twilio (actual implementation would use Twilio SDK)
  async sendViaChannel(phoneNumber, message, channel) {
    // In production, this would use Twilio API:
    // const client = require('twilio')(accountSid, authToken);
    // 
    // if (channel === 'whatsapp') {
    //   return await client.messages.create({
    //     from: `whatsapp:${twilioWhatsAppNumber}`,
    //     to: `whatsapp:${phoneNumber}`,
    //     body: message
    //   });
    // } else {
    //   return await client.messages.create({
    //     from: twilioPhoneNumber,
    //     to: phoneNumber,
    //     body: message
    //   });
    // }

    // Simulated response for demo
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          sid: `SM${Date.now()}`,
          status: 'sent',
          dateCreated: new Date()
        });
      }, 500);
    });
  }

  // Process confirmation response from client
  processConfirmation(appointmentId, response) {
    const reminder = this.reminders?.find(r => r?.appointmentId === appointmentId);
    
    if (!reminder) {
      return { success: false, error: 'Reminder not found' };
    }

    let confirmationStatus = 'unknown';
    let action = 'none';

    // Parse response (1 = confirm, 2 = cancel, 3 = reschedule)
    if (response === '1' || response?.toLowerCase()?.includes('تأكيد')) {
      confirmationStatus = 'confirmed';
      action = 'confirmed';
    } else if (response === '2' || response?.toLowerCase()?.includes('إلغاء')) {
      confirmationStatus = 'cancelled';
      action = 'cancelled';
    } else if (response === '3' || response?.toLowerCase()?.includes('تغيير')) {
      confirmationStatus = 'reschedule_requested';
      action = 'reschedule';
    }

    // Update reminder
    reminder.confirmationStatus = confirmationStatus;
    reminder.confirmationReceived = true;
    reminder.confirmedAt = new Date()?.toISOString();
    reminder.clientResponse = response;

    // Track confirmation
    const confirmation = {
      id: `CONF-${Date.now()}`,
      reminderId: reminder?.id,
      appointmentId: appointmentId,
      status: confirmationStatus,
      action: action,
      response: response,
      receivedAt: new Date()?.toISOString()
    };

    this.confirmations?.push(confirmation);
    this.notifyListeners();

    return { success: true, confirmation, action };
  }

  // Get reminder status for appointment
  getReminderStatus(appointmentId) {
    const reminder = this.reminders?.find(r => r?.appointmentId === appointmentId);
    return reminder || null;
  }

  // Get all reminders
  getAllReminders() {
    return this.reminders;
  }

  // Get confirmation statistics
  getConfirmationStats() {
    const total = this.reminders?.length;
    const confirmed = this.confirmations?.filter(c => c?.status === 'confirmed')?.length;
    const cancelled = this.confirmations?.filter(c => c?.status === 'cancelled')?.length;
    const reschedule = this.confirmations?.filter(c => c?.status === 'reschedule_requested')?.length;
    const pending = total - (confirmed + cancelled + reschedule);

    return {
      total,
      confirmed,
      cancelled,
      reschedule,
      pending,
      confirmationRate: total > 0 ? ((confirmed / total) * 100)?.toFixed(1) : 0
    };
  }

  // Toggle logo signature on/off
  toggleLogoSignature(enabled) {
    this.logoSignatureEnabled = enabled;
    this.notifyListeners();
    return this.logoSignatureEnabled;
  }

  // Get logo signature status
  getLogoSignatureStatus() {
    return this.logoSignatureEnabled;
  }

  // Update company footer settings
  updateCompanyFooter(footerConfig) {
    this.companyFooter = { ...this.companyFooter, ...footerConfig };
    this.notifyListeners();
    return this.companyFooter;
  }

  // Get company footer settings
  getCompanyFooter() {
    return this.companyFooter;
  }

  // Update company name
  updateCompanyName(name) {
    this.companyName = name;
    this.notifyListeners();
    return this.companyName;
  }
}

// Export singleton instance
export const reminderService = new ReminderService();
export default reminderService;